import type { ReactNode } from 'react';
import { Box, LinearProgress, Skeleton, Stack, type SxProps } from '@mui/material';
import { ApiErrorAlert } from '../ApiErrorAlert';

export interface QueryLike<T> {
  isPending: boolean;
  isError: boolean;
  error: unknown;
  data: T | undefined;
  isFetching?: boolean;
}

type SkeletonKind = 'table' | 'form' | 'detail' | 'cards';
const SKELETON_KINDS: SkeletonKind[] = ['table', 'form', 'detail', 'cards'];
const isSkeletonKind = (v: unknown): v is SkeletonKind =>
  typeof v === 'string' && (SKELETON_KINDS as string[]).includes(v);

export interface QueryBoundaryProps<T> {
  query: QueryLike<T>;
  children: (data: T) => ReactNode;
  skeleton?: SkeletonKind | ReactNode;
  skeletonRows?: number;
  /** `data` geldiğinde "boş" sayılıp `emptyState` gösterilecek mi? */
  isEmpty?: (data: T) => boolean;
  emptyState?: ReactNode;
  /** İlk yükleme bittikten sonra arka planda yeniden getirirken önceki içeriği ince
   *  bir ilerleme çubuğuyla koru (varsayılan). */
  keepPreviousWhileFetching?: boolean;
  errorSx?: SxProps;
}

function DefaultSkeleton({ kind, rows }: { kind: SkeletonKind; rows: number }) {
  if (kind === 'cards') {
    return (
      <Stack spacing={2}>
        {Array.from({ length: Math.min(rows, 4) }).map((_, i) => (
          <Skeleton key={i} variant="rounded" height={96} />
        ))}
      </Stack>
    );
  }
  if (kind === 'detail' || kind === 'form') {
    return (
      <Stack spacing={1.5}>
        {Array.from({ length: rows }).map((_, i) => (
          <Skeleton key={i} variant="rounded" height={kind === 'form' ? 40 : 28} />
        ))}
      </Stack>
    );
  }
  // table
  return (
    <Box>
      <Skeleton variant="rounded" height={40} sx={{ mb: 0.5 }} />
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} variant="rounded" height={36} sx={{ mb: 0.5 }} />
      ))}
    </Box>
  );
}

/** Yükleme / hata / boş / veri dallarını tek yerde toplar — sayfalardaki dağınık
 *  `CircularProgress` + `ApiErrorAlert` + elle boş-metin üçlüsünün yerine geçer. */
export function QueryBoundary<T>({
  query,
  children,
  skeleton = 'table',
  skeletonRows = 6,
  isEmpty,
  emptyState,
  keepPreviousWhileFetching = true,
  errorSx = { mb: 2 },
}: QueryBoundaryProps<T>) {
  if (query.isError && query.data === undefined) {
    return <ApiErrorAlert error={query.error} sx={errorSx} />;
  }

  if (query.isPending || query.data === undefined) {
    if (isSkeletonKind(skeleton)) {
      return <DefaultSkeleton kind={skeleton} rows={skeletonRows} />;
    }
    return <>{skeleton}</>;
  }

  const empty = isEmpty?.(query.data) ?? false;
  const fetching = keepPreviousWhileFetching && !!query.isFetching;

  return (
    <>
      {query.isError && <ApiErrorAlert error={query.error} sx={errorSx} />}
      {fetching && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}
      {empty ? emptyState ?? null : children(query.data)}
    </>
  );
}
